import clsx from 'clsx';
import type * as React from 'react';

import { type TableColumnDefinition, table } from '@gitbook/doc-core';
import type { EditorContainer } from '@gitbook/slate-react';
import { HeadlessTextInput } from '@gitbook/spine-react';

import { TextDiff } from '../../../Diff';
import { textAlignmentClassName } from '../../text-alignment';
import { getColumnTitle } from './definitions';
import { getColumnTextAlignment } from './text';
import type { RecordValueDisplayMode } from './types';

interface DefinitionTitleViewProps {
    /** Key of the parent Table block */
    nodeKey: string;

    /** Editor instance */
    editor: EditorContainer;

    /** Is this value editable ? */
    readOnly: boolean;

    /** Definition of the column */
    columnDef: TableColumnDefinition;

    /** Style of the column */
    className?: string;

    /** Display mode, in the grid-view's header cell or the dialog (for both grid and cards view) */
    displayMode?: RecordValueDisplayMode;
}

/**
 * View for a definition.
 */
export function DefinitionTitleView(
    props: Omit<DefinitionTitleViewProps, 'fixedStyles'> & {
        diff?: {
            before: string;
            after: string;
        };
    }
): React.ReactElement {
    const { columnDef, readOnly, className, displayMode = 'cell', diff } = props;

    // Styles applied to the input regardless of read-only or editable
    const fixedStyles = clsx(
        'flex w-full flex-1 truncate ',
        displayMode === 'modal' ? 'text-ui-heading-small' : 'font-bold text-secondary',
        // Only in grid-view we adhere to the alignment setting on the column definition.
        displayMode === 'modal' ? null : textAlignmentClassName[getColumnTextAlignment(columnDef)]
    );

    if (diff) {
        return <TextDiff before={diff.before} after={diff.after} />;
    }

    if (readOnly) {
        return (
            <HeadlessTextInput
                inputClassName={clsx(fixedStyles, className)}
                placeholder={getColumnTitle(columnDef)}
                value={columnDef.title}
                readOnly
            />
        );
    }

    return <DefinitionTitleViewEditable {...props} fixedStyles={fixedStyles} />;
}

/**
 * Editable definition title view.
 */
function DefinitionTitleViewEditable(
    props: DefinitionTitleViewProps & { fixedStyles: string }
): React.ReactElement {
    const { editor, nodeKey, columnDef, className, fixedStyles } = props;

    const value = columnDef.title || '';
    const placeholder = getColumnTitle(columnDef);

    return (
        // TODO: Make debounce possible in HeadlessTextInput
        <HeadlessTextInput
            inputClassName={clsx(fixedStyles, className)}
            placeholder={placeholder}
            value={value}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                editor.change((change) => {
                    table.renameColumn(change, nodeKey, columnDef.id, event.target.value);
                });
            }}
        />
    );
}
